import * as React from 'react';
import { motion } from 'motion/react';
import { 
  Zap, 
  TrendingUp, 
  BarChart3, 
  Activity, 
  ArrowUpRight, 
  ShieldCheck, 
  AlertTriangle,
  Info,
  Search, XCircle,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { type PreInflectionResult } from '../services/geminiService';
import { Tooltip } from './Tooltip';

interface PreInflectionDetectorProps {
  ticker: string;
  onTickerChange: (ticker: string) => void;
  onDetect: (ticker: string) => void;
  loading: boolean;
  result: PreInflectionResult | null;
  error: string | null;
  loadingMessage?: string;
  currentQuote?: { text: string; author: string };
  onAnalyze?: (ticker: string) => void;
}

export const PreInflectionDetector: React.FC<PreInflectionDetectorProps> = ({
  ticker,
  onTickerChange,
  onDetect,
  loading,
  result,
  error,
  loadingMessage = "Scanning for Inflection Signals...",
  currentQuote,
  onAnalyze,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const getStatusColor = (status: 'positive' | 'neutral' | 'negative') => {
    switch (status) {
      case 'positive': return 'text-success-700 bg-success-50 border-success-100';
      case 'neutral': return 'text-warning-700 bg-warning-50 border-warning-100';
      case 'negative': return 'text-danger-600 bg-danger-50 border-danger-100';
    }
  };

  const getStatusIcon = (status: 'positive' | 'neutral' | 'negative') => {
    switch (status) {
      case 'positive': return <TrendingUp className="w-4 h-4" />;
      case 'neutral': return <Activity className="w-4 h-4" />;
      case 'negative': return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getStageLabel = (stage: 1 | 2 | 3 | 4) => {
    switch (stage) {
      case 1: return 'Pre-Inflection';
      case 2: return 'Early Inflection';
      case 3: return 'Mid-Stage Compounder';
      case 4: return 'Late-Stage / Fully Priced';
    }
  };

  return (
    <div className="space-y-8">
      {/* Search Header */}
      <div className="oi-card p-5 md:p-10">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-oxblood-50 border border-oxblood/20 text-[10px] font-medium uppercase tracking-widest text-oxblood mb-6">
            <Zap className="w-3 h-3" />
            <span>Emerging Compounder Signal</span>
          </div>
          <h2 className="text-4xl font-serif font-medium mb-4 text-ink">
            Pre-Inflection <span className="italic">Detector</span>
          </h2>
          <p className="text-ink-500 mb-8 text-lg">
            Identify the subtle signals of a business re-rating before the market catches on.
          </p>

          <div className="flex items-center gap-3 max-w-md mx-auto">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (ticker.trim()) onDetect(ticker.toUpperCase());
              }}
              className="relative group flex-1"
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Enter ticker (e.g. POWL, NVDA)..."
                value={ticker}
                onChange={(e) => onTickerChange(e.target.value.toUpperCase())}
                className="w-full bg-surface-2 border border-transparent rounded-md py-4 pl-12 pr-10 focus:outline-none focus:ring-2 focus:ring-oxblood/20 focus:bg-surface focus:border-oxblood/30 transition-all text-sm font-medium placeholder:text-ink-500"
              />

              {/* Search icon — left */}
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 group-focus-within:text-oxblood transition-colors pointer-events-none" />

              {/* Clear button — right, only when input has text */}
              {ticker && (
                <button
                  type="button"
                  onClick={() => {
                    onTickerChange('');
                    inputRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-oxblood transition-colors"
                  aria-label="Clear search"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </form>

            {/* Detect button — beside the input */}
            <button
              type="button"
              onClick={() => {
                if (ticker.trim()) onDetect(ticker.toUpperCase());
              }}
              disabled={loading || !ticker.trim()}
              className="shrink-0 bg-oxblood text-paper text-xs font-medium px-6 py-4 rounded-md hover:bg-oxblood-800 transition-all disabled:opacity-50 active:scale-95 flex items-center gap-2 whitespace-nowrap"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <span>Detect</span>
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className={cn(
          "p-6 border rounded-lg flex items-center gap-4",
          error.includes("not listed") ? "bg-warning-50 border-warning-100 text-warning-700" : "bg-danger-50 border-danger-100 text-danger-600"
        )}>
          <AlertTriangle className={cn("w-6 h-6 shrink-0", error.includes("not listed") ? "text-warning-600" : "")} />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {loading && !result && (
        <div className="py-20 flex flex-col items-center justify-center gap-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-ink-200 border-t-oxblood rounded-full animate-spin"></div>
            <Zap className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-oxblood animate-pulse" />
          </div>
          <div className="text-center">
            <h3 className="text-lg font-serif font-medium italic mb-2">{loadingMessage}</h3>
            <p className="text-xs text-ink-500 uppercase tracking-widest font-medium">Pre-Inflection Analysis in Progress</p>
          </div>

          {currentQuote && (
            <motion.blockquote
              key={currentQuote.text}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 border-l-2 border-oxblood pl-5 text-left max-w-md"
            >
              <p className="font-serif text-lg text-ink leading-relaxed">{currentQuote.text}</p>
              <footer className="mt-3 text-ink-500 text-[10px] font-bold uppercase tracking-widest">— {currentQuote.author}</footer>
            </motion.blockquote>
          )}
        </div>
      )}

      {result && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Main Verdict Card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 oi-card p-5 md:p-10 relative overflow-hidden">
              <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <Tooltip content="The current stage of the business inflection lifecycle.">
                      <div className={cn(
                        "px-3 py-1 rounded-md text-[10px] font-medium uppercase tracking-widest cursor-help",
                        result.isPreInflection ? "bg-success-50 text-success-700" : "bg-surface-2 text-ink-600"
                      )}>
                        {result.isPreInflection ? `Stage ${result.stage}: ${getStageLabel(result.stage)}` : "No Inflection Signal"}
                      </div>
                    </Tooltip>
                    {result.isReInflection && (
                      <Tooltip content="This business was previously in decline but is now showing signs of a fundamental turnaround.">
                        <div className="px-3 py-1 rounded-md text-[10px] font-medium uppercase tracking-widest bg-oxblood-50 text-oxblood flex items-center gap-1 cursor-help">
                          <TrendingUp className="w-3 h-3" />
                          <span>Re-Inflection Candidate</span>
                        </div>
                      </Tooltip>
                    )}
                    <div className="text-ink-500 text-xs font-mono">{result.ticker}</div>
                    <Tooltip content="The primary factor expected to drive shareholder returns in the near term.">
                      <div className="px-2 py-0.5 rounded bg-surface-2 text-[8px] font-medium uppercase tracking-widest text-ink-500 border border-ink-200 cursor-help">
                        {result.driverOfReturns}
                      </div>
                    </Tooltip>
                  </div>

                  <h3 className="text-4xl font-serif font-medium italic mb-3 leading-tight">
                    {result.name}
                  </h3>

                  {typeof result.currentPrice === 'number' && result.currentPrice > 0 && (
                    <div className="flex items-baseline gap-3 mb-6">
                      <span className="font-mono text-2xl font-medium text-ink">${result.currentPrice.toFixed(2)}</span>
                      {typeof result.priceChangePercent === 'number' && (
                        <span className={cn(
                          "font-mono text-sm font-medium",
                          result.priceChangePercent >= 0 ? "text-success-700" : "text-danger-600"
                        )}>
                          {result.priceChangePercent >= 0 ? '+' : ''}{result.priceChangePercent.toFixed(2)}%
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-center md:items-end gap-4 shrink-0">
                  <Tooltip content="Overall confidence score in the inflection thesis (0-100).">
                    <div className={cn(
                      "w-24 h-24 rounded-lg flex flex-col items-center justify-center border-2 cursor-help",
                      result.isPreInflection ? "border-success-600 text-success-600 bg-success-50" : "border-ink-200 text-ink-500 bg-surface-2"
                    )}>
                      <span className="text-3xl font-mono font-medium leading-none">{result.confidenceScore}</span>
                      <span className="text-[8px] font-medium uppercase tracking-widest mt-1">Score</span>
                    </div>
                  </Tooltip>
                  
                  <div className="flex gap-2">
                    <Tooltip content="Business Quality Score: Evaluates ROE, margins, and demand durability.">
                      <div className="bg-surface-2 border border-ink-200 rounded-md p-2 text-center min-w-[60px] cursor-help">
                        <div className="text-[8px] font-medium text-ink-500 uppercase tracking-widest mb-1">BQS</div>
                        <div className="text-sm font-mono font-medium text-ink">{result.businessQualityScore}</div>
                      </div>
                    </Tooltip>
                    <Tooltip content="Inflection Opportunity Score: Evaluates acceleration magnitude and recognition gap.">
                      <div className="bg-surface-2 border border-ink-200 rounded-md p-2 text-center min-w-[60px] cursor-help">
                        <div className="text-[8px] font-medium text-ink-500 uppercase tracking-widest mb-1">IOS</div>
                        <div className="text-sm font-mono font-medium text-ink">{result.inflectionOpportunityScore}</div>
                      </div>
                    </Tooltip>
                    <Tooltip content="Volatility Score: Measures the consistency of fundamental signals. Lower is more stable.">
                      <div className="bg-surface-2 border border-ink-200 rounded-md p-2 text-center min-w-[60px] cursor-help">
                        <div className="text-[8px] font-medium text-ink-500 uppercase tracking-widest mb-1">VOL</div>
                        <div className="text-sm font-mono font-medium text-ink">{result.volatilityScore}%</div>
                      </div>
                    </Tooltip>
                    <Tooltip content="Monetization Proof: Percentage of revenue contributed by the new growth driver.">
                      <div className="bg-surface-2 border border-ink-200 rounded-md p-2 text-center min-w-[60px] cursor-help">
                        <div className="text-[8px] font-medium text-ink-500 uppercase tracking-widest mb-1">MON</div>
                        <div className="text-sm font-mono font-medium text-ink">{result.newRevenueContribution}%</div>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div className="max-w-3xl">
                <p className="text-ink-600 text-lg leading-relaxed mb-8">
                  {result.verdict}
                </p>

                <div className="flex flex-col sm:flex-row items-stretch gap-4">
                  <Tooltip content="The single most important takeaway from this analysis.">
                    <div className="p-4 bg-oxblood text-paper rounded-md flex-1 cursor-help">
                      <div className="text-[8px] font-medium uppercase tracking-[0.2em] text-paper/60 mb-1">Key Takeaway</div>
                      <p className="text-sm font-medium leading-snug">{result.keyTakeaway}</p>
                    </div>
                  </Tooltip>
                  <Tooltip content="The recommended investment action based on the current lifecycle stage.">
                    <div className="p-4 bg-surface-2 border border-ink-200 rounded-md flex-1 cursor-help">
                      <div className="text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500 mb-1">Suggested Action</div>
                      <p className="text-sm font-medium text-ink leading-snug">{result.suggestedAction}</p>
                    </div>
                  </Tooltip>
                </div>

                {onAnalyze && result && (
                  <div className="mt-6 pt-6 border-t border-ink-200">
                    <button
                      type="button"
                      onClick={() => onAnalyze(result.ticker)}
                      className="flex items-center gap-2 px-5 py-3 bg-oxblood text-paper text-xs font-medium uppercase tracking-widest rounded-md hover:bg-oxblood-800 transition-all active:scale-95"
                    >
                      <ChevronRight className="w-4 h-4" />
                      Run Oracle Analysis
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Tooltip content="Analysis of how the market narrative is shifting relative to fundamental proof.">
              <div className="oi-card p-5 md:p-10 flex flex-col justify-between cursor-help">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink-500">Narrative Shift</div>
                    <div className={cn(
                      "px-2 py-1 rounded text-[8px] font-medium uppercase tracking-widest border",
                      result.narrativeEvidenceTier === 3 ? "border-success-600 text-success-700" : 
                      result.narrativeEvidenceTier === 2 ? "border-warning-600 text-warning-700" : 
                      "border-danger-600 text-danger-600"
                    )}>
                      Tier {result.narrativeEvidenceTier} Evidence
                    </div>
                  </div>
                  <p className="text-xl font-serif leading-relaxed text-ink-600">
                    {result.narrativeShift}
                  </p>
                </div>
                <div className="mt-10 pt-10 border-t border-ink-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                      <Info className="w-4 h-4 text-oxblood" />
                    </div>
                    <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Oracle Insight Layer</span>
                  </div>
                </div>
              </div>
            </Tooltip>
          </div>

          {/* Signals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {Object.entries(result.signals).map(([key, signal]) => (
              <Tooltip key={key} content={signal.description}>
                <div className="oi-card p-6 hover:border-oxblood/30 transition-all group cursor-help">
                  <div className={cn(
                    "w-10 h-10 rounded-md flex items-center justify-center mb-4 border transition-colors",
                    getStatusColor(signal.status)
                  )}>
                    {getStatusIcon(signal.status)}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-[10px] font-medium uppercase tracking-widest text-ink-500 group-hover:text-oxblood transition-colors">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <div className={cn(
                      "text-[8px] font-medium uppercase tracking-widest px-1.5 py-0.5 rounded border",
                      signal.confidence === 'High' ? "text-success-700 border-success-100 bg-success-50" :
                      signal.confidence === 'Medium' ? "text-warning-700 border-warning-100 bg-warning-50" :
                      "text-ink-500 border-ink-200 bg-surface-2"
                    )}>
                      {signal.confidence}
                    </div>
                  </div>
                  <div className="text-xs font-medium mb-3 font-mono truncate" title={signal.metrics}>{signal.metrics}</div>
                  <p className="text-[10px] text-ink-500 leading-relaxed font-medium line-clamp-4">
                    {signal.description}
                  </p>
                </div>
              </Tooltip>
            ))}
          </div>

          {/* Historical Context */}
          <div className="oi-card p-5 md:p-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-oxblood rounded-md flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-paper" />
              </div>
              <div>
                <h4 className="text-sm font-medium uppercase tracking-widest text-ink">Historical Reconstruction</h4>
                <p className="text-xs text-ink-500">12-18 Month Retrospective Analysis</p>
              </div>
            </div>
            <div className="prose prose-sm max-w-none text-ink-600 leading-relaxed">
              {result.historicalContext}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
